import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  LocalHospital,
  Security,
  People,
  TrendingUp,
  Inventory,
  Assessment,
} from "@mui/icons-material";
import { HOSPITAL_NAME, LOGO_URL } from "./config";
import { useTranslation } from "react-i18next";

const WelcomePage = () => {
  const { t } = useTranslation();
  const [fadeIn, setFadeIn] = useState(false);
  const [activeCard, setActiveCard] = useState(0);

  useEffect(() => {
    setFadeIn(true);
    const interval = setInterval(() => {
      setActiveCard((prev) => (prev + 1) % 6);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: <LocalHospital sx={{ fontSize: 50 }} />,
      title: "Patient Management",
      desc: "Comprehensive patient records",
      color: "#0B7AAA",
    },
    {
      icon: <Inventory sx={{ fontSize: 50 }} />,
      title: "Inventory Control",
      desc: "Real-time stock tracking",
      color: "#0B7AAA",
    },
    {
      icon: <Assessment sx={{ fontSize: 50 }} />,
      title: "Financial Reports",
      desc: "Detailed analytics & insights",
      color: "#0B7AAA",
    },
    {
      icon: <Security sx={{ fontSize: 50 }} />,
      title: "Data Security",
      desc: "Enterprise-grade protection",
      color: "#0B7AAA",
    },
    {
      icon: <People sx={{ fontSize: 50 }} />,
      title: "HR Management",
      desc: "Employee & payroll system",
      color: "#0B7AAA",
    },
    {
      icon: <TrendingUp sx={{ fontSize: 50 }} />,
      title: "Sales Tracking",
      desc: "Monitor performance metrics",
      color: "#0B7AAA",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0B7AAA 0%, #064d6b 100%)",
        py: 6,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)",
        },
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            textAlign: "center",
            opacity: fadeIn ? 1 : 0,
            transform: fadeIn ? "translateY(0)" : "translateY(30px)",
            transition: "all 1.2s cubic-bezier(0.4, 0, 0.2, 1)",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Typography
            variant="h1"
            sx={{
              color: "white",
              fontWeight: 800,
              mb: 2,
              fontSize: { xs: "2.5rem", md: "4rem" },
              textShadow: "0 4px 20px rgba(0,0,0,0.3)",
              letterSpacing: "-0.02em",
            }}
          >
            {t("print.pharmacyName", { defaultValue: HOSPITAL_NAME })} 
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: "rgba(255,255,255,0.95)",
              mb: 6,
              fontWeight: 300,
              maxWidth: 800,
              mx: "auto",
            }}
          >
            Advanced Pharmacy Management System
          </Typography>

          <Box
            sx={{
              width: "50%",
              height: "40vh",
              mx: "auto",
              mb: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={LOGO_URL}
              alt={t("print.pharmacyName", { defaultValue: HOSPITAL_NAME })}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                filter: "drop-shadow(0 10px 30px rgba(0,0,0,0.3))",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </Box>

          <Grid container spacing={3} sx={{ mb: 6 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    background:
                      activeCard === index
                        ? "linear-gradient(135deg, #0B7AAA 0%, #064d6b 100%)"
                        : "rgba(255,255,255,0.95)",
                    backdropFilter: "blur(10px)",
                    borderRadius: 4,
                    p: 3,
                    transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                    border:
                      activeCard === index
                        ? "2px solid rgba(255,255,255,0.3)"
                        : "2px solid transparent",
                    boxShadow:
                      activeCard === index
                        ? "0 20px 60px rgba(11,122,170,0.4)"
                        : "0 10px 30px rgba(0,0,0,0.1)",
                    transform:
                      activeCard === index ? "translateY(-10px) scale(1.05)" : "translateY(0)",
                    "&:hover": {
                      transform: "translateY(-15px) scale(1.05)",
                      boxShadow: "0 25px 70px rgba(11,122,170,0.5)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: "50%",
                        background:
                          activeCard === index
                            ? "rgba(255,255,255,0.2)"
                            : "rgba(11,122,170,0.1)",
                        color: activeCard === index ? "white" : feature.color,
                        transition: "all 0.3s ease",
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 1,
                        fontWeight: 700,
                        color: activeCard === index ? "white" : "#1a1a1a",
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          activeCard === index
                            ? "rgba(255,255,255,0.9)"
                            : "text.secondary",
                        lineHeight: 1.6,
                      }}
                    >
                      {feature.desc}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Button
            variant="contained"
            size="large"
            sx={{
              px: 8,
              py: 2.5,
              fontSize: "1.2rem",
              fontWeight: 600,
              backgroundColor: "white",
              color: "#0B7AAA",
              borderRadius: 3,
              boxShadow: "0 15px 40px rgba(0,0,0,0.3)",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                transform: "translateY(-3px) scale(1.05)",
                boxShadow: "0 20px 50px rgba(0,0,0,0.4)",
              },
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            Get Started →
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default WelcomePage;
